import { action, observable } from "mobx";
import { Helper } from "../../../Helper";
import { IApplication, IDefaultValues } from "../../../interfaces/IApplication";
import { IApprover } from "../../../interfaces/IApprover";
import { IComposerComponent } from "../../../interfaces/IComposerTool";
import { IFeed } from "../../../interfaces/IFeed";
import { IHttpStatus } from "../../../interfaces/IHttpStatus";
import { IInstance } from "../../../interfaces/IInstance";
import { IMessageIAConfiguration } from "../../../interfaces/IMessage";
import { INode } from "../../../interfaces/INode";
import { ITargetingApplications } from "../../../interfaces/ITargetingApplications";
import { ITargetingExistingStrucuture } from "../../../interfaces/ITargetingExistingStructure";
import { ITargetingFields } from "../../../interfaces/ITargetingFields";
import { ITargetingProperties } from "../../../interfaces/ITargetingProperties";
import { ITenantSettings } from "../../../interfaces/ITenantSettings";
import { IUser } from "../../../interfaces/IUser";
import CategoryService from "../../../services/CategoryService";
import { LocalizationService } from "../../../services/LocalizationService";
import { TargetingService } from "../../../services/TargetingService";
import { IFontSlots } from "../../../interfaces/IFontSlots";
import { IRecurrenceData } from "../../../interfaces/IRecurrenceData";

export class Store {

  @observable public id: number;
  @observable public open: boolean;
  @observable public token: string;
  @observable public tenant: string;
  @observable public environment: "Development" | "Test" | "Production";
  @observable public instance: IInstance;
  @observable public component: "Messages" | "Event" | "ContentCreator" | "ContentOrganizer";
  @observable public user: IUser;
  @observable public userRole: "Administrator" | "Editor" | "Reader";
  @observable public author: IUser;
  @observable public highlightColor: string;
  @observable public placeholderColor: string;
  @observable public imageList: string;
  @observable public videoList: string;
  @observable public isMobile: boolean;
  @observable public startDate: any;
  @observable public endDate: any;
  @observable public allDayEvent: boolean;
  @observable public isBotInstalled: boolean;
  @observable public sendUsingBot: boolean;
  @observable public sendUsingBotOriginal?: boolean;
  @observable public creatingNewMessage: boolean;
  @observable public applicationSettings: ITargetingApplications;
  @observable public push: boolean;
  @observable public pushOriginal?: boolean;
  @observable public targetingExistingStructure: ITargetingExistingStrucuture;
  @observable public iaTargetingTargetContent: boolean;
  @observable public iaTargetingOperator: string = "OR";
  @observable public iaTargetingOperatorADGroups: string;
  @observable public targetingFields: ITargetingFields[];
  @observable public iaTargetingGroupData: string;
  @observable public isDraft: boolean;
  @observable public isDraftOriginally: boolean;
  @observable public approvalStatus: "Pending" | "Approved" | "Rejected";
  @observable public originalApprovalStatus: string;
  @observable public tenantSettings: ITenantSettings;
  @observable public likesEnabledOnInstance: boolean;
  @observable public commentsEnabledOnInstance: boolean;
  @observable public approvers: IApprover[];
  @observable public isReviewProcess: boolean;
  @observable public likesEnabled: boolean;
  @observable public commentsEnabled: boolean;
  @observable public marginTop: number;
  @observable public dataAutomationIdPrefix: string;
  @observable public isFullPageView: boolean;
  @observable public galleryImagesToUploadCount: number;
  @observable public galleryImagesUploaded: number;
  @observable public activeGalleryIndex: number;
  @observable public isAdminPage: boolean;
  @observable public isPinned: boolean;
  @observable public pinnedEndDate: Date;
  @observable public isNewsLink?: boolean;
  @observable public newsLink?: string;
  @observable public openNewsLinkInNewTab?: boolean;
  @observable public isMessagesTemplateEditor?: boolean;
  @observable public fontSlots?: IFontSlots;
  @observable public isCreatingFromTemplate?: boolean;

  @observable public activeComponent: IComposerComponent;
  @observable public contentComponents: IComposerComponent[] = [];
  @observable public loading: boolean;
  @observable public componentToAdd: IComposerComponent;
  @observable public startIndex: number;
  @observable public selectedComposerTab: "content" | "settings" | "preview" | "replay" | "newsLink" = "content";

  @observable public cover: IComposerComponent;
  @observable public subjects: IComposerComponent;
  @observable public title: IComposerComponent;
  @observable public teaser: IComposerComponent;
  @observable public location: IComposerComponent;
  @observable public contacts: IComposerComponent;
  @observable public attachments: IComposerComponent;
  @observable public htmlContent: string;
  @observable public configuration: IMessageIAConfiguration = {
    replay: {
      id: "Replay",
      type: "cover",
      typeToEdit: "image",
      boxStyle: undefined,
      content: undefined,
      image: {
        source: "Link",
      },
      video: {
        source: "Link",
      },

    },

    fieldsToHide: []
  };

  @observable public isSaving: boolean;
  @observable public isSavingDraft: boolean;
  @observable public showLineToolbox: boolean;
  @observable public isGalleryImagesUploading: boolean;
  @observable public isUploadingVideoOrImage: boolean;

  @observable public showStockPhotos: boolean;
  @observable public showOrganizationalAssets: boolean;

  @observable public selectedFeeds: string[];
  @observable public feedOptions: IFeed[];
  @observable public publishToReplay: boolean;
  @observable public replayContentTooHigh: boolean;

  @observable public hasInitializedSettings: boolean;

  @observable public recurrenceInfo?: {
    isRecurring?: boolean;
    recurrenceData?: IRecurrenceData;
    isEditingOccurrence?: boolean;
    occurrenceSeriesId?: number;
    occurrenceStartDate?: Date;
  };

  // Editing Components

  @action public addComponent(component: IComposerComponent): void {
    this.loading = true;
    if (component.id === undefined) {
      component.id = Helper.getRandomStringKey();
    }
    if (component.type === "image" && !component.image) {
      component.image = {
        source: "Upload",
        imageOrientation: "Custom",
        width: 620,
        borderRadius: 5
      };
    }
    if (component.type === "video" && !component.video) {
      component.video = {
        source: "Link",
        posterImage: {
          source: "Upload"
        }
      };
    }
    if (component.type === "audio" && !component.audio) {
      component.audio = {
        source: "Link"
      };
    }
    if (component.type === "gallery" && !component.gallery) {
      component.gallery = {
        source: "Upload",
        type: "Tiles",
        maxImages: 10,
        images: [],
        borderRadius: 5,
        sortOrder: "TimeLastModified desc"
      };
    }
    if (component.type === "button" && !component.button) {
      component.button = {
        text: undefined,
        url: "",
        align: "left",
        textSize: 14,
        borderRadius: 0,
        backgroundColor: this.highlightColor,
        textColor: Helper.getTextColorBasedOnBackgroundColorDarkness(this.highlightColor, "#fff", "#000")
      };
    }

    if (component.type === "divider" && !component.divider) {
      component.divider = {
        color: "#000",
        length: 100,
        weight: 4
      };
    }


    this.contentComponents.push(component);
    setTimeout(() => {
      this.activeComponent = this.contentComponents[this.contentComponents.length - 1];
    }, 0);
    this.loading = false;
    this.componentToAdd = undefined;
    this.showLineToolbox = false;
  }

  @action public addComponentAtIndex(index: number): void {
    this.loading = true;
    if (this.componentToAdd.id === undefined) {
      this.componentToAdd.id = Helper.getRandomStringKey();
    }
    if (this.componentToAdd.type === "image" && !this.componentToAdd.image) {
      this.componentToAdd.image = {
        source: "Upload",
        imageOrientation: "Custom"
      };
    }
    if (this.componentToAdd.type === "video" && !this.componentToAdd.video) {
      this.componentToAdd.video = {
        source: "Link"
      };
    }
    if (this.componentToAdd.type === "audio" && !this.componentToAdd.audio) {
      this.componentToAdd.audio = {
        source: "Link"
      };
    }

    if (this.componentToAdd.type === "gallery" && !this.componentToAdd.gallery) {
      this.componentToAdd.gallery = {
        source: "Upload",
        type: "Rotator",
        maxImages: 10,
        images: []
      };
    }
    this.contentComponents.splice(index, 0, this.componentToAdd);
    this.componentToAdd = undefined;
    setTimeout(() => {
      this.activeComponent = this.contentComponents[index];
    }, 0);
    this.loading = false;
    this.showLineToolbox = false;
  }

  @action public moveComponentAtIndex(newIndex: number): void {
    this.loading = true;
    this.contentComponents.splice(this.startIndex, 1);
    this.contentComponents.splice(newIndex > this.startIndex ? newIndex - 1 : newIndex, 0, this.componentToAdd);
    this.loading = false;
    this.startIndex = undefined;
    this.componentToAdd = undefined;
    this.showLineToolbox = false;
  }

  @action public removeComponentAtIndex(componentIndex: number): void {
    if (componentIndex) {
      this.contentComponents.splice(componentIndex, 1);
    } else {
      let indexToRemove;
      this.contentComponents.forEach((component: IComposerComponent, index: number) => {
        if (this.activeComponent && component.id === this.activeComponent.id) {
          indexToRemove = index;
        }
      });
      this.contentComponents.splice(indexToRemove, 1);
    }
  }

  @action public dublicateComponent() {
    const tempComponent: string = JSON.stringify(this.activeComponent);
    const dublicateComponent = Object.assign({}, JSON.parse(tempComponent));
    dublicateComponent.id = Helper.getRandomStringKey();
    this.componentToAdd = dublicateComponent;
    this.addComponent(this.componentToAdd);
  }

  // Categories

  @observable public categoryDataItems: any[];
  @observable public categoryNodes: INode[];

  @action public getCategories(forceUpdate?: boolean): Promise<void> {
    return new Promise((resolve) => {
      if (forceUpdate || this.categoryNodes === undefined) {
        CategoryService.getCategories(this.environment, this.instance?.instanceName, this.tenant, this.token, this.component === "Messages" ? "IntraActive-Messages" : "IntraActive-Events", this.component).then((category) => {
          if (category.length > 0) {
            this.categoryDataItems = category[0].data.items;
            console.log("categoryDataItems");
            console.log(this.categoryDataItems);
            this.categoryNodes = this.getChildCategories(category[0].data.items);
          } else {
            this.categoryDataItems = undefined;
            this.categoryNodes = undefined;
            this.subjects = undefined;
          }
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  @action private getChildCategories(category: any): any[] {
    if (category) {
      const childCategories = [];
      category.forEach((childData: any) => {
        childCategories.push(
          {
            defaultLabel: childData.name,
            id: childData.guid,
            childNodes: this.getChildCategories(childData.children)
          }
        );
      });
      return childCategories;
    } else {
      return undefined;
    }
  }

  // Targeting

  @observable public targetingProperties: ITargetingProperties[] = [];

  @action public async getTargetingApplication(): Promise<void> {
    return await TargetingService.getApplication(this.environment, this.tenant, this.token, this.component === "Messages" ? "Messages" : "Events", this.isAdminPage).then((result) => {
      if (result?.httpStatus?.statusCode === 200) {
        this.applicationSettings = result?.value?.length > 0 ? result?.value[0] : undefined;
        let application: IApplication;
        if (result.value.length > 0) {
          const appData = <IApplication>JSON.parse(result.value[0].defaultValues);
          let defaultValues: IDefaultValues;
          if (!appData || Array.isArray(appData)) {
            const list: any[] = [];
            defaultValues = {
              isEnable: false,
              type: "profileData",
              values: list,
              allValues: false
            };
            application = {
              id: undefined,
              title: result.value[0].title,
              name: result.value[0].title,
              instances: [],
              default: defaultValues
            };
          } else {
            application = appData;
            application.default.type = result?.value?.length > 0 && result?.value[0].applicationSettings?.toLocaleLowerCase() === "profile data" ? "profileData" : "userGroups";
          }
          if (this.applicationSettings) {
            this.applicationSettings.application = application;
          }
        }
      } else {
        console.log("ERROR: getTargetingApplication failed with " + result?.httpStatus?.statusCode);
      }
    });
  }

  @action public async getTargetingProperties(): Promise<void> {
    TargetingService.getProperties(this.environment !== undefined ? this.environment : "Production", this.tenant, this.token, this.isAdminPage).then((result) => {
      if (result?.httpStatus?.statusCode === 200) {
        this.targetingProperties = result.value;
      } else {
        console.log("ERROR: getTargetingProperties failed with " + result?.httpStatus?.statusCode);
      }
    });
  }

  // Links

  // Text
  @observable public showLinkEditor: boolean;
  @observable public textSelection: Selection;
  @observable public linkEditorRange: Range;
  @observable public linkEditorTitle: string;
  @observable public linkEditorUrl: string;
  @observable public linkEditorTarget: string;

  // Image
  @observable public linkEditorImageTarget: string;

  // Validation

  @observable public enableValidation: boolean;

  @action public validateForDraft(): boolean {
    return !(!this.title.text || this.title.text === "<h1><br></h1>");
  }

  @action public validate(): boolean {
    return this.validateComposeTab() && this.validateSettingsTab() && this.validateReplayTab();
  }

  @action public validateComposeTab(): boolean {
    return this.validateTitle() && this.validateContacts() && this.validateSubjects() && this.validateTeaser();
  }

  @action public validateSettingsTab(): boolean {
    return this.validateTargeting() && this.validateApprovers() && this.validatePinDate() && this.validateRecurrence();
  }

  @action public validateReplayTab(): boolean {
    if (!this.isNewsLink) {
      return ((this.validateReplayFeed() && this.validateReplayContentHeight()) || !this.publishToReplay);
    }
    return true;
  }

  @action public validateTitle(): boolean {
    if (this.enableValidation) {
      return !(!this.title.text || this.title.text === "<h1><br></h1>");
    }
    return true;
  }

  @action public validateTeaser(): boolean {
    if (this.instance?.settings?.type === "reels" && !this.instance?.settings?.enableTeaser) {
      return true;
    }
    if (this.enableValidation && this.instance?.settings?.enableTeaser && !this.isNewsLink) {
      return !(!this.teaser.text || this.teaser?.text?.length === 0);
    }
    return true;
  }

  @action public validateSubjects(): boolean {
    if (this.enableValidation && this.instance && this.instance.settings && this.instance.settings.categoriesRequired) {
      return this.subjects.categories.length > 0;
    }
    return true;
  }

  @action public validateContacts(): boolean {

    if (this?.configuration?.fieldsToHide?.includes("author")) {
      return true;
    }

    if (this.enableValidation && this.component !== "Event") {
      return this.contacts.users.length > 0;
    }
    return true;
  }

  @action public validateTargeting(): boolean {
    const isUsingProfileDataTargeting = this.applicationSettings?.applicationSettings?.toLocaleLowerCase() === "profile data";
    if (this.enableValidation && this.instance && this.instance.settings && this.instance.settings?.targetingRequired) {
      if (this.isDraftOriginally) {
        if (isUsingProfileDataTargeting && JSON.stringify(this.targetingFields) === `{"IATargetingDepartment":[],"IATargetingJobTitle":[],"IATargetingLocation":[],"IATargetingCustom1":[],"IATargetingCustom2":[]}`) {
          // very stupid check to see if there is no targeting on a draft when using profile data. Targeting is a mess. Sorry :(
          return false;
        }
        if (!isUsingProfileDataTargeting && (!this.iaTargetingGroupData || this.iaTargetingGroupData?.length < 2)) {
          return false;
        }
      }
      if (isUsingProfileDataTargeting && this.targetingFields !== undefined && JSON.stringify(this.targetingFields).length > 2) {
        return true;
      }
      return this.iaTargetingGroupData && this.iaTargetingGroupData?.length > 2;
    }
    if (this.enableValidation) {
      if (this.iaTargetingTargetContent && this.applicationSettings) {
        if (!isUsingProfileDataTargeting && !this.iaTargetingGroupData) {
          return false;
        }
        if (isUsingProfileDataTargeting && !this.targetingFields) {
          return false;
        }
      }
    }
    return true;
  }

  @action public validateApprovers(): boolean {
    if (this.enableValidation && this.instance && this.instance.approvalEnabled) {
      if (this.approvers && this.approvers.length > 0) {
        let activeApprovers = 0;
        this.approvers.forEach((approver: IApprover) => {
          if (!approver.disabled) {
            activeApprovers++;
          }
        });
        return activeApprovers > 0;
      } else {
        return false;
      }
    }
    return true;
  }

  @action public validatePinDate(): boolean {
    if (this.enableValidation && this.instance?.settings?.enablePinMessages && this.isPinned && !this.pinnedEndDate) {
      return false;
    }
    return true;
  }

  @action public validateRecurrence(): boolean {
    if (this.enableValidation && this.recurrenceInfo?.isRecurring && this.recurrenceInfo?.recurrenceData?.recurrenceType === "weekly") {
      return this.recurrenceInfo.recurrenceData.daysOfWeek?.length > 0;
    }
    return true;
  }

  @action public validateReplayFeed(): boolean {
    if (this.enableValidation && this.publishToReplay) {
      return this.selectedFeeds?.length > 0;
    }
    return true;
  }

  @action public validateReplayContentHeight(): boolean {
    if (this.enableValidation && this.publishToReplay) {
      return !this.replayContentTooHigh;
    }
    return true;
  }

  @observable public serverAlert: IHttpStatus;

  @action public getServerError(statusCode: number): string {
    const localizationService = new LocalizationService();
    switch (statusCode) {
      case 401: // Unauthorized
        return localizationService.strings.Messages_ServerError401;
      case 403: // Forbidden
        return localizationService.strings.Messages_ServerError403;
      case 500: // Internal Server Error
        return localizationService.strings.Messages_ServerError500;
      case 503: // Service Unavailable
        return localizationService.strings.Messages_ServerError503;
      default: // Bad Request
        return localizationService.strings.Messages_ServerError400;
    }
  }

}